import Helpers from "./helpers";
import Typeahead from "./typeahead";
import typeaheadAutocompleteTemplates from "../templates/typeahead";
import AlgoliaTracking from "./algolia-tracking";
import Lockout from "./lockout";
import Logger from "./logger";

declare var Mustache: any;

enum SearchType {
	Category = 'category',
	Product = 'product',
	Content = 'content'
}

export default class SearchAutocompleteMobile {

	private searchContainerElement = document.querySelector('#mobile-search') as HTMLDivElement;
	private searchElement = document.querySelector('#mobileSearchText') as HTMLInputElement;
	private searchToggleElement = document.querySelector('#headerSearchToggle') as HTMLLinkElement;
	private autocompleteIsSetup = false;
	private searchOpen = false;
	private allow = true;
	private minCharLimit = 3;
	private searchCounts = [
		{
			name: SearchType.Category,
			count: 0
		},
		{
			name: SearchType.Product,
			count: 0
		},
		{
			name: SearchType.Content,
			count: 0
		}
	];
	private abortControllers: Array<any> = [];

	constructor() {

		if (!this.searchElement || document.querySelector('.c-header--checkout')) {
			return;
		}

		if (!this.searchContainerElement) {
			Logger.trackException(new Error('SearchAutocompleteMobile: No search container element found.'));
			return;
		}

		this.attachEvents();
	}

	private attachEvents() {

		if (document.querySelector('.js-adonis-mobile-search')) {
			this.initAutocomplete();
		} else {
			this.searchToggleElement.addEventListener('click', (e: Event) => {
				e.preventDefault();
				this.showSearch();
				this.initAutocomplete();
				this.searchToggleElement.classList.add('js-typeahead-done');
			});
		}

		this.searchContainerElement.querySelector('.js-close').addEventListener('click', (e: Event) => {
			e.preventDefault();
			this.hideSearch();
		});

		this.searchContainerElement.querySelector('.js-intro').addEventListener('click', (e: Event) => {
			e.preventDefault();
			this.hideSearch();
		});

		this.searchElement.addEventListener('input', () => {
			if (this.searchElement.value.length < this.minCharLimit) {
				this.resetBlock();
			}
		});

		const debouncedHideCheck = Helpers.debounce(() => {
			if (window.matchMedia('(min-width:980px)').matches && this.searchOpen) {
				this.hideSearch();
			}
		}, 150);

		window.addEventListener('resize', debouncedHideCheck.bind(this));

	}

	private resetBlock() {
		const resultsContainer = document.querySelector('.js-results') as HTMLDivElement;
		resultsContainer.style.display = 'none';
		document.querySelector('.js-results-categories, .js-results-products, .js-results-content').classList.add('loading');
		Helpers.empty(document.querySelector('.js-results-categories-data, .js-results-products-data, .js-results-content-data'));
		document.body.classList.remove('mobile-results-open');
		document.documentElement.classList.remove('has-search-open');
		this.searchOpen = false;
		this.resetSearchCounts();
		Lockout.off();
	}

	private updateBlock(searchType: SearchType, query: string, url: string, template: string, element: HTMLElement, parent: HTMLElement, doSearch = true) {

		if (parent) {
			parent.classList.add('loading');
		}

		if (document.querySelector('.js-adonis-mobile-search')) {
			window.scrollTo(0, 0);
			document.body.classList.add('mobile-results-open');
			document.documentElement.classList.add('has-search-open');
			const resultsContainer = document.querySelector('.js-results') as HTMLDivElement;
			resultsContainer.style.display = "block";
			Lockout.on();
		}
		this.searchOpen = true;

		if (!doSearch) {
			Mustache.parse(template);

			element.innerHTML = Mustache.render(template, {
				Query: query,
				HasResults: false,
				Results: [],
				TotalResults: 0,
				MaxResults: 0
			});

			const resultsContainer = this.searchContainerElement.querySelector('.js-results') as HTMLDivElement;
			if (resultsContainer) {
				resultsContainer.style.display = query.length >= this.minCharLimit ? "block" : "none";
			}

			return;
		}

		let searchCountIndex = this.searchCounts.findIndex((searchCount) => searchCount.name === searchType);
		this.searchCounts[searchCountIndex].count++;

		let abortController = new AbortController();
		let signal = abortController.signal;
		this.abortControllers.push(abortController);

		fetch(`${url}?mobile=true&triggered-searches=${this.searchCounts[searchCountIndex].count}&term=${encodeURIComponent(query)}`, {
			signal: signal
		}).then((response: any) => {

			if (response.ok) {
				return response.json();
			} else {
				throw new Error(response as any);
			}
		}).then((data) => {
			const isProductsSearch = searchType === SearchType.Product;
			const results = isProductsSearch ? data.Items : data;

			AlgoliaTracking.updateAlgoliaFromSearchResponse(data?.Attributes);

			if (isProductsSearch) {
				for (let i = 0; i < results.length; i++) {
					const currentProduct = results[i];
					currentProduct.Url = AlgoliaTracking.addAlgoliaQueryToURL(results[i].Url, results[i].Guid, 'Autocomplete');
				}
			}

			Mustache.parse(template);

			element.innerHTML = Mustache.render(template, {
				Query: query,
				HasResults: results.length > 0,
				Results: results,
				TotalResults: results.length,
				MaxResults: isProductsSearch ? data.TotalResults : 0
			});

			const suggestions = element.querySelectorAll('.js-highlightable');
			Typeahead.highlightResults(suggestions, query);

			if (parent) {
				parent.classList.remove('loading');
			}

			const resultsContainer = document.querySelector('.js-results') as HTMLDivElement;
			resultsContainer.style.display = "block";

			if (isProductsSearch) {
				this.attachAlgoliaTracking();
			}

			if (query.length < this.minCharLimit) {
				this.hideSearch();
			}

		}).catch((error) => {

			if (error.name !== 'AbortError') {
				this.hideSearch();
			}

		});

	}

	private attachAlgoliaTracking() {
		const productLinks = this.searchContainerElement.querySelectorAll('.js-autocomplete-search-result-link');
		productLinks.forEach((link) => {
			link.addEventListener('click', (e) => {
				const targetElement = e.currentTarget as HTMLLinkElement;
				const productGuid = targetElement.getAttribute('data-product-guid');
				const position = Helpers.getElementIndex(targetElement.closest('.js-autocomplete-search-result-item')) + 1;
				AlgoliaTracking.clickedObjectID(productGuid, position, "Autocomplete: Product Clicked");
			});
		});

	}

	private initAutocomplete() {

		if (this.autocompleteIsSetup) {
			return;
		}

		new Typeahead(this.searchElement, {
			charLimit: this.minCharLimit,
			handleOwnData: true,
			queryReceived: (query) => {

				// categories
				this.updateBlock(
					SearchType.Category,
					query,
					this.searchElement.getAttribute('data-typeahead-category'),
					typeaheadAutocompleteTemplates.mobileCategories,
					document.querySelector('.js-results-categories-data'),
					document.querySelector('.js-results-categories')
				);

				// products
				this.updateBlock(
					SearchType.Product,
					query,
					this.searchElement.getAttribute('data-typeahead-product'),
					typeaheadAutocompleteTemplates.mobileProducts,
					document.querySelector('.js-results-products-data'),
					document.querySelector('.js-results-products')
				);

				// content
				this.updateBlock(
					SearchType.Content,
					query,
					this.searchElement.getAttribute('data-typeahead-content'),
					typeaheadAutocompleteTemplates.mobileContent,
					document.querySelector('.js-results-content-data'),
					document.querySelector('.js-results-content')
				);

			},
			onInputChange: (query) => {

				this.abortRequests();

				// categories
				this.updateBlock(
					SearchType.Category,
					query,
					this.searchElement.getAttribute('data-typeahead-category'),
					typeaheadAutocompleteTemplates.mobileCategories,
					document.querySelector('.js-results-categories-data'),
					document.querySelector('.js-results-categories'),
					false
				);

				// products
				this.updateBlock(
					SearchType.Product,
					query,
					this.searchElement.getAttribute('data-typeahead-product'),
					typeaheadAutocompleteTemplates.mobileProducts,
					document.querySelector('.js-results-products-data'),
					document.querySelector('.js-results-products'),
					false
				);

				// content
				this.updateBlock(
					SearchType.Content,
					query,
					this.searchElement.getAttribute('data-typeahead-content'),
					typeaheadAutocompleteTemplates.mobileContent,
					document.querySelector('.js-results-content-data'),
					document.querySelector('.js-results-content'),
					false
				);

			},
			requests: []
		});

		this.searchElement.setAttribute('autocomplete', 'off')

		this.autocompleteIsSetup = true;

	}

	private abortRequests() {

		if (!this.abortControllers.length) return;
		for (let i = 0; i < this.abortControllers.length; i++) {
			this.abortControllers[i].abort();
		}
		this.abortControllers = [];

	}

	private showSearch() {

		if (!this.allow || window.matchMedia('(min-width:980px)').matches || this.searchOpen) return false;
		this.allow = false;
		const context = this;

		Lockout.on();
		Helpers.fadeIn(this.searchContainerElement, 'block', () => {
			context.allow = true;
			context.searchElement.focus();
			context.searchOpen = true;
		});
	}

	private hideSearch() {

		if (!this.allow || !this.searchOpen) return false;
		this.allow = false;
		const context = this;

		Lockout.off();

		if (document.querySelector('.js-adonis-mobile-search')) {
			this.resetBlock();
		} else {
			Helpers.fadeOut(this.searchContainerElement, () => {
				context.allow = true;
				context.searchOpen = false;
			});
		}

		this.resetSearchCounts();

	}

	private resetSearchCounts() {
		for (let i = 0; i < this.searchCounts.length; i++) {
			this.searchCounts[i].count = 0;
		}
	}

}

